import { memo, useEffect, useState } from 'react';
import { SalesforceProduct } from '~/lib/salesforce/types';
import { ResetButton } from '~/shared/components/Button/ResetButton';
import Spinner from '~/shared/components/Spinner/Spinner';
import { Typography } from '~/shared/components';
import { useTranslation } from '~/shared/utils/translation';
import { ReservationInfoMessage } from '~/features/product-details/components/ReservationDrawer/ReservationInfoMessage';
import { PDPStoreList } from '~/features/product-details/components/ReservationDrawer/styled';
import type {
    ReservationStoreBase,
    ReservationStoreCtaIntent,
} from '~/features/product-details/components/ReservationDrawer/types';
import styles from './ReservationStoreList.module.scss';
import { ReservationStoreListItem } from './ReservationStoreListItem';

const PAGE_SIZE = 20;

type Props = {
    product: SalesforceProduct;
    storesList?: ReservationStoreBase[];
    isFetching?: boolean;
    searchError: string;
    isStoresWithStockOnly: boolean;
    pendingStoreCtaId: string | null;
    onStoreCtaClick: (intent: ReservationStoreCtaIntent) => void;
    onAccordionOpen: (shopId: string, index: number) => void;
    quantity: number;
};

export const ReservationStoreList = memo(
    ({
        storesList,
        isFetching,
        searchError,
        isStoresWithStockOnly,
        product,
        pendingStoreCtaId,
        onStoreCtaClick,
        onAccordionOpen,
        quantity,
    }: Props) => {
        const { translate } = useTranslation();
        const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

        const filteredStoresList = isStoresWithStockOnly
            ? storesList?.filter((store) => (store.c_stock_level ?? 0) >= 1)
            : storesList;

        // Collapse back to the first page whenever the list changes (new search/geo/default) or
        // the stock filter toggles, so "show more" always starts fresh.
        useEffect(() => {
            setVisibleCount(PAGE_SIZE);
        }, [storesList, isStoresWithStockOnly]);

        const renderInfoMessage = (message: string) => (
            <PDPStoreList>
                <ReservationInfoMessage>{message}</ReservationInfoMessage>
            </PDPStoreList>
        );

        if (searchError) {
            return renderInfoMessage(searchError);
        }

        if (!isFetching && filteredStoresList && filteredStoresList.length === 0) {
            return renderInfoMessage(translate('message.noStoresWithStock'));
        }

        const visibleStores = filteredStoresList?.slice(0, visibleCount);
        const remainingCount = (filteredStoresList?.length ?? 0) - visibleCount;
        const hasMore = remainingCount > 0;
        const nextBatchCount = Math.min(PAGE_SIZE, remainingCount);

        return (
            <>
                <PDPStoreList>
                    {isFetching ? (
                        <div style={{ margin: 'auto' }}>
                            <Spinner />
                        </div>
                    ) : (
                        visibleStores?.map((store, index) => (
                            <ReservationStoreListItem
                                key={store.id}
                                store={store}
                                index={index}
                                product={product}
                                quantity={quantity}
                                isStoreCtaPending={pendingStoreCtaId !== null}
                                pendingStoreCtaId={pendingStoreCtaId}
                                onStoreCtaClick={onStoreCtaClick}
                                onAccordionOpen={onAccordionOpen}
                            />
                        ))
                    )}
                </PDPStoreList>
                {!isFetching && hasMore && (
                    <div className={styles.showMoreWrapper}>
                        <ResetButton
                            className={styles.showMoreButton}
                            type="button"
                            onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                        >
                            <Typography variant="paragraph-M">
                                {translate('pdp.showMoreStores', {
                                    count: nextBatchCount.toString(),
                                })}
                            </Typography>
                        </ResetButton>
                    </div>
                )}
            </>
        );
    },
);

ReservationStoreList.displayName = 'ReservationStoreList';
